<template>
  <section
    class="card card-small themed-entity-form"
    :role="modal ? 'dialog' : 'region'"
    :aria-modal="modal ? 'true' : null"
    :aria-label="title"
    :tabindex="modal ? -1 : null"
  >
    <header class="themed-entity-form__header">
      <div class="themed-entity-form__heading">
        <span class="themed-entity-form__icon" aria-hidden="true">
          <span class="material-icons">{{ icon }}</span>
        </span>
        <div>
          <div v-if="eyebrow" class="themed-entity-form__eyebrow">{{ eyebrow }}</div>
          <h2>{{ title }}</h2>
        </div>
      </div>
      <button v-if="showClose" type="button" class="themed-entity-form__close" :aria-label="closeLabel" @click="$emit('cancel')">
        <span class="material-icons" aria-hidden="true">close</span>
      </button>
    </header>

    <div class="themed-entity-form__body">
      <slot />
    </div>

    <footer v-if="$slots.actions" class="themed-entity-form__footer">
      <slot name="actions" />
    </footer>
  </section>
</template>

<script>
// Focusable descendants, in document order. Used to keep Tab inside the dialog: aria-modal tells a
// screen reader that everything outside is inert, and without a trap that is a promise the markup
// does not keep — Tab walks straight out into the page behind.
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export default {
  name: 'ThemedEntityForm',
  props: {
    title: {
      type: String,
      required: true,
    },
    eyebrow: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: 'edit_note',
    },
    closeLabel: {
      type: String,
      default: 'Close',
    },
    modal: {
      type: Boolean,
      default: true,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    focusFirstControl: {
      type: Boolean,
      default: true,
    },
  },
  mounted() {
    if (!this.modal) return;
    // Where focus was before the dialog opened, so it can be handed back on close rather than
    // dropped to the top of the page.
    this.previouslyFocused = document.activeElement;
    document.addEventListener('keydown', this.onKeydown);
    this.$nextTick(this.focusFirst);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    if (this.modal && this.previouslyFocused && this.previouslyFocused.focus) {
      this.previouslyFocused.focus();
    }
  },
  methods: {
    focusables() {
      return Array.from(this.$el.querySelectorAll(FOCUSABLE))
        .filter((el) => el.offsetParent !== null);
    },
    // The first field, not the close button: opening a form should land where the work is. Falls
    // back to the dialog itself so focus is never left outside it.
    focusFirst() {
      if (!this.focusFirstControl) {
        this.$el.focus();
        return;
      }
      const target = this.focusables().find((el) => !el.classList.contains('themed-entity-form__close'));
      (target || this.$el).focus();
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('cancel');
        return;
      }
      if (event.key !== 'Tab') return;

      const items = this.focusables();
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      // Wrap at both ends, and pull focus back in if it has already escaped.
      if (event.shiftKey && (active === first || !this.$el.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.$el.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
.themed-entity-form {
  border: 1px solid #e3ebf1;
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(31, 57, 80, .16);
  margin: 0;
  max-height: min(88vh, 44rem);
  overflow: auto;
  padding: 0;
  width: min(calc(100vw - 2rem), 36rem) !important;
}

.themed-entity-form__header {
  align-items: center;
  border-bottom: 1px solid #e8eef3;
  display: flex;
  justify-content: space-between;
  padding: 1.1rem 1.25rem;
}

.themed-entity-form__heading { align-items: center; display: flex; gap: .75rem; min-width: 0; }
.themed-entity-form__icon { align-items: center; background: rgba(0, 152, 184, .1); border-radius: 10px; color: var(--fitnet-primary); display: inline-flex; flex: 0 0 2.6rem; height: 2.6rem; justify-content: center; }
.themed-entity-form__icon .material-icons { font-size: 1.35rem; }
.themed-entity-form__heading h2 { color: #254766; font-size: 1.12rem; font-weight: 700; margin: 0; overflow-wrap: anywhere; }
.themed-entity-form__eyebrow { color: #8192a5; font-size: .68rem; font-weight: 700; letter-spacing: .08em; margin-bottom: .12rem; text-transform: uppercase; }
.themed-entity-form__close { align-items: center; background: transparent; border: 0; border-radius: 7px; color: #71859a; cursor: pointer; display: inline-flex; flex: 0 0 2.25rem; height: 2.25rem; justify-content: center; padding: 0; }
.themed-entity-form__close:hover,
.themed-entity-form__close:focus { background: #f1f6f8; color: #254766; }
.themed-entity-form__close:focus-visible { outline: 2px solid rgba(0, 152, 184, .35); outline-offset: 2px; }
.themed-entity-form__body { padding: 1.35rem 1.25rem; }
.themed-entity-form__footer { align-items: center; background: #fbfcfd; border-top: 1px solid #e8eef3; display: flex; gap: .6rem; justify-content: flex-end; padding: .9rem 1.25rem; }

@media (max-width: 575px) {
  .themed-entity-form__header,
  .themed-entity-form__body,
  .themed-entity-form__footer { padding-inline: 1rem; }
}
</style>
